import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import "./CustomFilterComponent.css";

/**
 * Generic multi-select filter modal.
 *
 * Props:
 *   open           {boolean}   - controls visibility
 *   handleClose    {function}  - called when modal is dismissed
 *   filterConfig   {Array}     - categories to display:
 *                                  [{ key, label, options: [{ label, value }] }]
 *   selectedFilters {Object}   - current selections keyed by filterConfig[].key
 *                                  e.g. { priority: ["High"], prepSlot: [] }
 *   onApplyFilter  {function}  - called with updated selectedFilters on Apply
 */
export default function CustomFilterComponent({
  open,
  handleClose,
  filterConfig,
  selectedFilters,
  onApplyFilter,
  variant,
}) {
  const [activeKey, setActiveKey] = useState(filterConfig?.[0]?.key || "");
  const [localSelections, setLocalSelections] = useState({});
  const [searchText, setSearchText] = useState("");
  const overlayRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLocalSelections(
        Object.fromEntries(filterConfig?.map((c) => [c?.key, [...(selectedFilters?.[c?.key] || [])]]) || [])
      );
      setActiveKey(filterConfig?.[0]?.key || "");
      setSearchText("");
    }
  }, [open]);

  useEffect(() => {
    function onKeyDown(e) {
      if (e?.key === "Escape") handleClose?.();
    }
    if (open) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose]);

  if (!open) return null;

  const activeCategory = filterConfig?.find((c) => c?.key === activeKey);
  const filteredOptions = activeCategory
    ? activeCategory?.options?.filter((opt) =>
        opt?.label?.toLowerCase()?.includes(searchText?.toLowerCase())
      )
    : [];

  function toggleOption(key, value) {
    setLocalSelections((prev) => {
      const current = prev?.[key] || [];
      return {
        ...prev,
        [key]: current?.includes(value) ? current?.filter((v) => v !== value) : [...current, value],
      };
    });
  }

  function handleClear() {
    setLocalSelections(Object.fromEntries(filterConfig?.map((c) => [c?.key, []]) || []));
  }

  function handleApply() {
    onApplyFilter?.(localSelections);
    handleClose?.();
  }

  function handleOverlayClick(e) {
    if (e?.target === overlayRef?.current) handleClose?.();
  }

  const showLeftPanel = filterConfig?.length > 1;

  const isPanel = variant === "panel";

  return (
    <div
      className={`cfc-overlay${isPanel ? " cfc-overlay--panel" : ""}`}
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div className={`cfc-modal${isPanel ? " cfc-modal--panel" : ""}`}>
        {/* Body */}
        <div className="cfc-body">
          {/* Left Panel */}
          {showLeftPanel && (
            <div className="cfc-left-panel">
              <span className="cfc-panel-heading">Filters</span>
              {filterConfig?.map((cat) => {
                const hasSelections = (localSelections?.[cat?.key] || [])?.length > 0;
                const isActive = activeKey === cat?.key;
                return (
                  <div
                    key={cat?.key}
                    className={`cfc-category-item${isActive ? " cfc-category-item--active" : ""}`}
                    onClick={() => {
                      setActiveKey(cat?.key);
                      setSearchText("");
                    }}
                  >
                    <span className="cfc-category-label">{cat?.label}</span>
                    {hasSelections && <span className="cfc-category-dot" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Right Panel */}
          <div className="cfc-right-panel">
            <p className="cfc-right-title">{activeCategory?.label}</p>

            <div className="cfc-search-wrap">
              <MagnifyingGlassIcon className="cfc-search-icon" />
              <input
                className="cfc-search-input"
                type="text"
                placeholder="Search by Category"
                value={searchText}
                onChange={(e) => setSearchText(e?.target?.value)}
              />
            </div>

            <div className="cfc-options-list">
              {filteredOptions?.length > 0 ? (
                filteredOptions?.map((opt) => {
                  const checked = (localSelections?.[activeKey] || [])?.includes(opt?.value);
                  return (
                    <label key={opt?.value} className="cfc-option-row">
                      <input
                        type="checkbox"
                        className="cfc-checkbox"
                        checked={checked}
                        onChange={() => toggleOption(activeKey, opt?.value)}
                      />
                      <span className="cfc-option-label">{opt?.label}</span>
                    </label>
                  );
                })
              ) : (
                <span className="cfc-empty">No options found.</span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="cfc-footer">
          <button className="cfc-btn-clear" onClick={handleClear}>
            <XMarkIcon className="cfc-btn-icon" />
            Clear
          </button>
          <button className="cfc-btn-apply" onClick={handleApply}>
            Apply
            <CheckIcon className="cfc-btn-icon" />
          </button>
        </div>
      </div>
    </div>
  );
}

CustomFilterComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  filterConfig: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  selectedFilters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  onApplyFilter: PropTypes.func.isRequired,
  variant: PropTypes.string,
};

CustomFilterComponent.defaultProps = {
  variant: undefined,
};
